<script setup lang="ts">
import {computed, ref, watch, markRaw, onUnmounted, type Component} from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { usePredicateStore } from "@/stores/predicate-store";
import { useElementPredicates } from '@/composables/useElementPredicates';
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useAuthDataStore } from "@/stores/auth-data";
import { handleError } from "@/appdata/globals";
import GuidedStepper from '../GuidedStepper.vue';
import CharacterWorldStep from './steps/CharacterWorldStep.vue';
import CharacterAncestryStep from './steps/CharacterAncestryStep.vue';
import CharacterHindrancesStep from './steps/CharacterHindrancesStep.vue';
import CharacterAttributesStep from './steps/CharacterAttributesStep.vue';
import CharacterSkillsStep from './steps/CharacterSkillsStep.vue';
import CharacterEdgesStep from './steps/CharacterEdgesStep.vue';
import CharacterGearStep from './steps/CharacterGearStep.vue';
// Rendered BEFORE the stepper: the steps' own detail drawers share its z-index, and coming later in
// the DOM they slide in over the concept panel rather than under it.
import CharacterConceptPanel from './CharacterConceptPanel.vue';
import type { SwadeCharacterInterface } from "@/types/characters";

interface StepConfig {
    key: string;
    label: string;
    component: Component;
    skip?: () => boolean;
}

const ux = useUxData();
const store = useAppDataStore();
const characterData = useCharacterDataStore();
const predicateStore = usePredicateStore();
const setupStore = useGuidedSetupStore();
const auth = useAuthDataStore();

// Reset shared build state at flow start so prior-run totals/buildOptions don't leak
// into this flow. combineSettingRules memoizes on buildOptions, so without this reset
// the next combineSettingRules call would short-circuit and return the previous run's
// rules even when called with the new character's world.
characterData.resetBuildValidation();

const character = ref<SwadeCharacterInterface | null>(null);
const hasAncestries = ref<boolean | null>(null);

const allStepConfigs: StepConfig[] = [
    {
        key: 'world',
        // This step also owns the character name, so it is never skipped — users without
        // world access simply get the naming half of it (see CharacterWorldStep).
        label: auth.canSelectWorld
            ? (ux.labels?.guidedSetup?.character?.steps?.world ?? 'World')
            : (ux.labels?.tools?.modalCharacterNew?.name?.label ?? 'Name'),
        component: markRaw(CharacterWorldStep),
    },
    {
        key: 'ancestry',
        label: ux.labels?.guidedSetup?.character?.steps?.ancestry ?? 'Ancestry',
        component: markRaw(CharacterAncestryStep),
        skip: () => hasAncestries.value === false,
    },
    {
        key: 'hindrances',
        label: ux.labels?.guidedSetup?.character?.steps?.hindrances ?? 'Hindrances',
        component: markRaw(CharacterHindrancesStep),
    },
    {
        key: 'attributes',
        label: ux.labels?.guidedSetup?.character?.steps?.attributes ?? 'Attributes',
        component: markRaw(CharacterAttributesStep),
    },
    {
        key: 'skills',
        label: ux.labels?.guidedSetup?.character?.steps?.skills ?? 'Skills',
        component: markRaw(CharacterSkillsStep),
    },
    {
        key: 'edges',
        label: ux.labels?.guidedSetup?.character?.steps?.edges ?? 'Edges',
        component: markRaw(CharacterEdgesStep),
    },
    {
        key: 'gear',
        label: ux.labels?.guidedSetup?.character?.steps?.gear ?? 'Gear',
        component: markRaw(CharacterGearStep),
        skip: () => characterData.buildOptions?.startingFunds?.type === 'wealth',
    },
];

const activeStepConfigs = computed(() => {
    return allStepConfigs.filter(config => !config.skip?.());
});

const steps = computed(() => {
    return activeStepConfigs.value.map(config => ({ label: config.label }));
});

watch(steps, (newSteps) => {
    setupStore.setTotalSteps(newSteps.length);
}, { immediate: true });

watch(() => setupStore.characterDraft, (draft) => {
    if (draft) {
        character.value = draft as SwadeCharacterInterface;
    }
}, { immediate: true });

watch (() => setupStore.currentStep, async (newStep, oldStep) => {
  if (!character.value) return;
  await characterData.recalculateCharacterPoints(
      character.value,
      store.system?.defaults ?? null,
      character.value.world ?? undefined,
  );
  await characterData.recalculateDerivedStats(character.value);
  await characterData.recalculateTotalPerkPoints(character.value);
  characterData.updateBuildStatus(character.value);
})

// Availability probe only — reads predicateStore.worlds / .sources, which the watcher
// below sets. Do NOT use predicateStore.elementPredicates here: that getter appends an
// element.className predicate derived from elementFilters.alias, which belongs to the
// world editor and would leak whatever tab was last opened there.
const { predicates: ancestryPredicates } = useElementPredicates('ancestries');

watch(() => character.value?.world, async (world) => {
    predicateStore.worlds = world ? [world] : [];
    // The Source filter is offered on the Ancestry step and every picker step, for every
    // draft, and its selection is carried between steps, so the flow — not the individual
    // steps — owns its lifetime: reset it whenever the draft's world changes, and again when
    // the flow itself goes away.
    predicateStore.sources = [];

    try {
        const response = await characterData.initializeElementChoices('ancestries', null, ancestryPredicates.value);
        hasAncestries.value = (response?.results?.length ?? 0) > 0;
    } catch (error) {
        hasAncestries.value = false;
        handleError(error);
    }
}, { immediate: true });

const onSubmit = async () => {
    if (!character.value) return;

    setupStore.setSubmitting(true);

    try {
        const saved = await store.persistCharacter(character.value);
        // persistCharacter handles a failed save itself and resolves nothing for it, so the rest of
        // this block would otherwise close the flow in edit mode as though the character were saved.
        // The store has already queued the error notification; stay on the step (as WorldSetupFlow does).
        if (!saved) return;

        store.props.edit = true;
        setupStore.completeFlow();
        setupStore.resetFlow();
    } catch (error) {
        handleError(error);
    } finally {
        setupStore.setSubmitting(false);
    }
};

onUnmounted(() => { predicateStore.sources = []; });

defineExpose({ character });
</script>

<template>
    <CharacterConceptPanel :character="character" />
    <GuidedStepper :steps="steps" @submit="onSubmit">
        <template v-for="(stepConfig, index) in activeStepConfigs" :key="stepConfig.key" #[`step-${index}`]>
            <component
                :is="stepConfig.component"
                v-model:character="character"
                :step-index="index"
            />
        </template>
    </GuidedStepper>
</template>
